import { getTagList } from '@/api/chat/chatSessionTag';
import {
    UserInfo,
    SessionItem,
    MessageItem,
    EndSessionParams,
    endSession,
    getSessionUsage,
    getSendMessageMode,
} from '@/api/chat/chatUser';
import { getField, updateField } from '@/libs/vuexHelper';
import i18n from '@/locales';
import { Message } from 'view-design';
import ChatLocalCache from '@/libs/ChatLocalCache';
import { cloneDeep } from 'lodash';

const chatLocalCache = new ChatLocalCache();

/**
 * Vue 2 deep reactivity 会给每条消息的每个嵌套字段建一对 getter/setter + Dep + Context,
 * 历史消息按 ~50 字段嵌套时,单条 1 KB 数据会膨胀到数十 KB。
 * 冻结消息后 Vue 2 的 observe() 直接 skip(`isExtensible` 为 false),消息保持 1 KB 量级。
 *
 * 注意:冻结之后想改动单条消息字段必须用 splice 整条替换,
 * 直接 `Vue.set` 在冻结对象上会静默失败(参考 ChatList.vue 的 replaceMessageAt)。
 */
const freezeMsg = <T extends object>(m: T): T => (
    m && typeof m === 'object' && !Object.isFrozen(m) ? Object.freeze(m) : m
);
const freezeMsgList = (list: MessageItem[]): MessageItem[] => list.map((m) => freezeMsg(m as any) as MessageItem);

const userInfoFieldsToUpdateMap = {
    remark_name: { stateKey: 'chatUserInfo', sessionKey: 'remark_name' },
    avatar: { stateKey: 'sessionInfo', sessionKey: 'avatar' },
    name: { stateKey: 'sessionInfo', sessionKey: 'title' },
    phone: { stateKey: 'sessionInfo', sessionKey: 'phone' },
    email: { stateKey: 'sessionInfo', sessionKey: 'email' },
};

export default {
    namespaced: true,
    state: {
        unread_count: 0, // 未读会话数 0-无 1-有
        session_num: 0, // 会话额度
        session_num_usage: 0, // 会话用量

        /** 筛选参数 start */
        filter_type: '0', // 0-全部(默认) 1-未读 2-未回
        sort_type: '0', // 0-最新排序 1-最旧排序
        filter_channel: ['all'], // 筛选渠道
        filterAccounts: { // 筛选渠道账号
            1: [],
            2: [],
            3: [],
            4: [],
            5: [],
            6: [],
            7: [],
            8: [],
            10: [],
            11: [],
            12: [],
            13: [],
            14: [],
            15: [],
            16: [],
            17: [],
            18: [],
            19: [],
            20: [],
            21: [],
            22: [],
            23: [],
            24: [],
        },
        userQuery: '', // 用户搜索词
        filterMore: {}, // 会话列表筛选更多
        filterViewId: '', // 会话列表筛选视图ID
        filterViewBarStatus: '0', // 视图列表状态 0-展开 1-收起为图标 2-隐藏视图
        filterStatus: 'active', // 会话列表状态筛选 active | inactive
        hasSessionFilter: false, // 是否筛选会话列表
        /** 筛选参数 end */

        /** 会话数 start */
        assigned_count: 0,
        unassigned_count: 0,
        bots_count: 0,
        /** 会话数 end */

        /** 全选操作 start */
        sessionCheckedType: '', // 进行多选的会话类型 assigned | unassigned | robot
        assignedCheckedSession: [], // 已分配列表多选列表（id）
        unassignedCheckedSession: [], // 未分配列表多选列表（id）
        robotCheckedSession: [], // 机器人列表多选列表（id）
        /** 全选操作 end */

        /** 帖子评论相关（8、13） start */
        postCommentData: {},
        postCommentSending: false,
        postCommentMessenger: false,
        postCommentMessengerLoading: false,
        /** 帖子评论相关（8、13） end */

        /** 结束会话相关start */
        openEndSession: false,
        endSessionLoading: false,
        fetchEndSessionLoading: false,
        hasTags: false,
        /** 结束会话相关end */

        tabValue: 'assigned', // 当前会话列表tab assigned | unassigned | robot
        rightTabValue: 'customerTab', // 右侧信息tab customerTab | sessionTab | commonTab | orderTab
        collapseTabs: false, // 是否收起右侧面板
        assignedSysUserId: '', // 已分配tab 查看的成员列表
        assignedSysUserCount: 0, // 已分配成员总数
        isBatchAssignSessionFinish: false, // 分配会话完成
        groupUserList: [], // 群组成员

        /** 打开的会话 start */
        activeUser: '', // 当前选中会话 chat_user_id
        chatUserLoading: false, // 请求访客信息中
        chatUserInfo: {}, // 访客信息
        sessionInfo: {}, // 会话信息
        newMessageList: [],
        selectedBotsList: false,
        selectedUnassignedList: false,
        selectedAssignedList: false,
        pinMessageList: [], // 当前会话中置顶消息的列表
        /** 打开的会话 end */

        /** 多选/粘贴文件发送 start */
        openPasteModal: false,
        pasteFiles: [],
        pasteCustomFileType: '',
        /** 多选/粘贴文件发送 end */

        /** 全局配置 start */
        recommendedAnswerForHelpLook: false, // 是否开启 HelpLook 推荐答案
        sendMessageMode: 'socket', // 发送消息模式 socket | http
        isRetrySendMessage: false, // 是否重试发送消息
        robotAutoReply: '1', // 会话接入是否保持托管状态 1-是 0-否
        isReceiveNoticeLogs: false, // 是否上传通知日志
        /** 全局配置 end */
        unsentMessageList: [], // 未发送的消息

        /** 聊天成员多选 start */
        assignedSysUserIdList: [], // 已分配tab 查看的成员列表
        groupMemberValueEnum: {}, // 分组筛选枚举列表，用于显示
        groupRealMemberValue: [], // 分组筛选列表，枚举处理过后的数组
        memberRealListValue: [], // 成员筛选列表，用于显示
        /** 聊天成员多选 end */

        /** 其他参数 start */
        isServerDisconnect: false,
        /** 其他参数 end */

        inputExpand: false, // 输入框是否展开

        fastSearchWay: '', // 筛选输入框快速搜索方式

        sendSocketStatus: false, // 发送消息的 socket 连接状态

        translateTextLoading: false, // 翻译文本loading
    },
    getters: {
        getField,
        hasUnReadMsg(state: any) {
            return state.unread_count > 0;
        },
        show_no_session_guide(state: any) {
            return !state.bots_count && !state.unassigned_count && !state.assigned_count && !state.hasSessionFilter;
        },
        show_no_session(state: any) {
            return !state.bots_count && !state.unassigned_count && !state.assigned_count;
        },
        noSelectedSession(state: any) {
            // activeUser 不在 所有列表 中
            return !(state.selectedBotsList || state.selectedAssignedList || state.selectedUnassignedList);
        },
        postCommentReplying(state: any) {
            return !!state.postCommentData.mid;
        },
        // 处于选择模式下
        isSelectionMode(state: any) {
            return state.assignedCheckedSession.length > 0 || state.unassignedCheckedSession.length > 0 || state.robotCheckedSession.length > 0;
        },
        isFilterSession(state: any) {
            return state.filterMore.phone ||
                state.filterMore.label_ids ||
                (Array.isArray(state.filterMore.range_time) && state.filterMore.range_time.length) ||
                (Array.isArray(state.filterMore.created_time) && state.filterMore.created_time.length) ||
                state.filter_type !== '0' ||
                state.sort_type !== '0' ||
                !state.filter_channel.includes('all' as any) ||
                !!state.userQuery || state.filterMore.email || !!state.filterMore.user_last_reply_time;
        },
        transformAssignedSysUserIdList(state: any) {
            return state.assignedSysUserIdList.includes('all') ? '' : state.assignedSysUserIdList.join(',');
        },
        // 是否显示会话总数
        showSessionTotal() {
            if ((window as any).ss_basic_config && (window as any).ss_basic_config.c_s_t) {
                return (window as any).ss_basic_config.c_s_t === '1';
            }
            return false;
        },
    },
    mutations: {
        updateField,
        setUnreadCount(state: any, value: any) {
            state.unread_count = value;
        },
        addUnreadCount(state: any) {
            state.unread_count = Number(state.unread_count) + 1;
        },
        setGroupUserList(state: any, value:any) {
            state.groupUserList = value;
        },
        setSendMessageMode(state: any, value: 'http' | 'socket') {
            state.sendMessageMode = value;
        },
        setIsRetrySendMessage(state: any, value: boolean) {
            state.isRetrySendMessage = value;
        },
        setFilterStatus(state: any, value: 'active' | 'inactive') {
            state.filterStatus = value;
        },
        // 设置当前选中会话
        setActiveUser(state: any, value: string) {
            state.activeUser = value;
            if (!value) {
                state.chatUserInfo = {};
                state.sessionInfo = {};
            }
        },
        setChatUserLoading(state: any, value: boolean) {
            state.chatUserLoading = value;
        },
        // 设置当前选中会话用户信息
        setChatUserInfo(state: any, value: UserInfo) {
            state.chatUserInfo = value;
        },
        // 设置当前选中会话信息
        setSessionInfo(state: any, value: SessionItem) {
            state.sessionInfo = value;
        },
        // 更新当前选中会话用户信息
        updateChatUserInfo(state: any, value: any) {
            let isCurrentSession = true;
            if (value.chat_user_id === state.chatUserInfo.chat_user_id) {
                /* 更新会话信息相关 start */
                const session: any = {};
                for (const [key, { stateKey, sessionKey }] of Object.entries(userInfoFieldsToUpdateMap)) {
                    if (value[key] && state[stateKey][key] !== value[key]) {
                        session[sessionKey] = value[key];
                    }
                }

                state.sessionInfo = {
                    ...state.sessionInfo,
                    ...session,
                };
                /* 更新会话信息相关 end */
                if (value.email && state.chatUserInfo?.channel_info?.content && state.chatUserInfo.channel_info.content.email !== undefined) {
                    state.chatUserInfo.channel_info.content.email = value.email;
                }
                if (value.phone && state.chatUserInfo?.channel_info?.content && state.chatUserInfo.channel_info.content.phone !== undefined) {
                    state.chatUserInfo.channel_info.content.phone = value.phone;
                }
                state.chatUserInfo = {
                    ...state.chatUserInfo,
                    ...value,
                };
            } else {
                isCurrentSession = false;
            }
            chatLocalCache.setUserInfoById(state.chatUserInfo.chat_user_id, state.chatUserInfo, isCurrentSession);
        },
        // 更新当前选中会话信息
        updateSessionInfo(state: any, session: any) {
            if (session.chat_user_id === state.activeUser) {
                state.sessionInfo = {
                    ...state.sessionInfo,
                    ...session,
                };
                // 更新访客信息（chatUserInfo）
                if (state.sessionInfo.chat_user_id === state.chatUserInfo.chat_user_id) {
                    let labels = cloneDeep(state.chatUserInfo.labels);
                    if (session.labels) {
                        labels = cloneDeep(session.labels);
                        labels.forEach((item: any) => {
                            item.id = item.id ? item.id.toString() : '';
                        });
                    }
                    state.chatUserInfo = {
                        ...state.chatUserInfo,
                        labels,
                        remark_name: state.sessionInfo.remark_name,
                        phone: state.sessionInfo.phone,
                        email: state.sessionInfo.email,
                    };
                    chatLocalCache.setUserInfoById(state.chatUserInfo.chat_user_id, state.chatUserInfo);
                }
            }
        },
        // 初始化最新消息列表
        initNewMessageList(state: any, messageList: MessageItem[]) {
            state.newMessageList = freezeMsgList(messageList);
        },
        // 设置新消息列表（20条）
        setNewMessageList(state: any, messageList: MessageItem[]) {
            state.newMessageList = freezeMsgList(messageList);
        },
        // 设置发送消息的 socket 的连接状态
        setSendSocketStatus(state: any, status: boolean) {
            state.sendSocketStatus = status;
        },
        /**
         * 重置当前会话上下文相关的所有状态。
         *
         * 在 iframe 被 shell 切走至 /idle 时调用,清掉当前会话才持有的数据:
         * 消息列表、访客信息、会话信息、群组成员、未发送消息、粘贴文件、帖子评论态等。
         *
         * 不动:筛选项 / tab 选中态 / 全局开关(sendMessageMode / robotAutoReply 等),
         * 这些是用户偏好,跨会话保留。
         *
         * 切断 V8 对 frozen MessageItem[] 的最后一条引用链,让 GC 真正回收 chat 历史数据。
         */
        resetSession(state: any) {
            state.activeUser = '';
            state.chatUserInfo = {};
            state.sessionInfo = {};
            state.newMessageList = [];
            state.pinMessageList = [];
            state.groupUserList = [];
            state.unsentMessageList = [];
            state.pasteFiles = [];
            state.pasteCustomFileType = '';
            state.openPasteModal = false;
            state.groupMemberValueEnum = {};
            state.groupRealMemberValue = [];
            state.memberRealListValue = [];
            state.postCommentData = {};
            state.postCommentSending = false;
            state.postCommentMessenger = false;
            state.postCommentMessengerLoading = false;
            state.openEndSession = false;
            state.endSessionLoading = false;
            state.fetchEndSessionLoading = false;
            state.inputExpand = false;
            state.fastSearchWay = '';
            state.translateTextLoading = false;
        },
    },
    actions: {
        fetchSessionUsage({ state }: any) {
            getSessionUsage()
                .then((res) => {
                    state.session_num = +res.data.data.session_num;
                    state.session_num_usage = +res.data.data.session_num_usage;
                })
                .catch(console.log);
        },
        fetchTagList({ state }: any) {
            return getTagList({}).then((res) => {
                // if (res.data.data.list.length) {
                //     state.hasTags = true;
                // }
                state.hasTags = Boolean(res.data.data.list.length);
            }).catch(console.log);
        },
        clickEndSession({ dispatch, state, rootState }: any, payload: EndSessionParams) {
            return new Promise((resolve, reject) => {
                state.fetchEndSessionLoading = true;
                dispatch('fetchTagList').then(() => {
                    // 判断是否弹出二次确认框，是否设置了标签或打开了二次确认
                    if (rootState.company.company.end_session_confirm || state.hasTags) {
                        // 需要打开
                        state.openEndSession = true;
                        resolve({
                            openEndSession: state.openEndSession,
                        });
                        state.fetchEndSessionLoading = false;
                    } else {
                        dispatch('handleCloseSession', payload).then((res: any) => {
                            resolve({
                                openEndSession: false,
                                res,
                            });
                        }).catch((err: any) => {
                            reject(err);
                        }).finally(() => {
                            state.fetchEndSessionLoading = false;
                        });
                    }
                }).catch((err: any) => {
                    reject(err);
                    state.fetchEndSessionLoading = false;
                });
            });
        },
        handleCloseSession({ state }: any, payload: EndSessionParams) {
            return new Promise((resolve, reject) => {
                state.endSessionLoading = true;
                // 请求结束会话
                endSession(payload).then((res) => {
                    Message.success({
                        content: i18n.t('chatPlugin.endSession.success'),
                        duration: 6,
                    });
                    state.openEndSession = false;
                    resolve(res);
                }).catch((err: any) => {
                    console.log(err);
                    reject(err);
                }).finally(() => {
                    state.endSessionLoading = false;
                });
            });
        },
        // 消息发送模式参数设置
        fetchSendMessageMode({ commit }: any) {
            getSendMessageMode().then((res) => {
                commit('setSendMessageMode', res.data.data.mode || 'socket');
                /**
                 * 是否开启重试模式：
                 * send_message_retry 为 1 并且 不默认使用 http 发送模式
                 */
                const _isRetrySendMessage = (res.data.data.send_message_retry || '0') === '1' && res.data.data.mode !== 'http';
                console.log('fetchSendMessageMode', _isRetrySendMessage);
                commit('setIsRetrySendMessage', _isRetrySendMessage);
            });
        },
        changeSession({ commit, state }: any, session: SessionItem) {
            /* 切换会话前置处理 start */
            // 关闭fb 评论回复状态
            if (state.sessionInfo.chat_user_id !== session.chat_user_id) {
                state.postCommentSending = false;
                state.postCommentMessenger = false;
                state.postCommentData = {};
                state.pinMessageList = [];
            }
            /* 切换会话前置处理 end */
            const chat_user_id = session.chat_user_id;
            commit('setActiveUser', chat_user_id);
            commit('setSessionInfo', session);
            commit('setChatUserLoading', true);
            chatLocalCache.getUserInfoById(chat_user_id, session, (data: UserInfo) => {
                if (data.chat_user_id === state.activeUser) {
                    data.isRemotely = '0';
                    commit('setChatUserInfo', data);
                }
            }).then((data: any) => {
                if (data.chat_user_id === state.activeUser) {
                    data.isRemotely = '1';
                    commit('setChatUserInfo', data);
                }
            }).catch((err: any) => {
                console.error('chatLocalCache getUserInfoById catch error', err);
            }).finally(() => {
                commit('setChatUserLoading', false);
            });
        },
        changeMessage({ commit }: any, chat_user_id: string) {
            return new Promise((resolve, reject) => {
                try {
                    chatLocalCache.getMessageById(chat_user_id, (data: MessageItem[]) => {
                        const frozen = freezeMsgList(data);
                        commit('initNewMessageList', frozen);
                        resolve(frozen);
                    });
                } catch (e) {
                    console.error(e);
                    reject(e);
                }
            });
        },
        updateNewMessageList({ commit, state }: any, { chat_user_id, messageList }: { chat_user_id: string; messageList: MessageItem[]; }) {
            return new Promise((resolve, reject) => {
                try {
                    const frozen = freezeMsgList(messageList);
                    commit('setNewMessageList', frozen);
                    chatLocalCache.setMessageById(chat_user_id, frozen);
                    resolve(true);
                } catch (e) {
                    console.error(e);
                    reject(e);
                }
            });
        },
        /**
         * 切到 /idle(shell 离开 iframe 路由)时调用,集中释放当前会话相关的内存。
         *
         * - commit('resetSession'):清 Vuex 中的会话级状态
         * - chatLocalCache.clearMemoryCache():清进程内存里的消息/访客缓存数组与挂着的 xhr
         *
         * IndexDB 不动,下次切回旧版聊天仍能走快路径回填。
         */
        releaseChatResources({ commit }: any) {
            commit('resetSession');
            chatLocalCache.clearMemoryCache();
        },
        resetChatUserInfo({ commit, state }: any, chat_user_id: string) {
            return new Promise((resolve, reject) => {
                // @ts-ignore
                chatLocalCache.fetchUserInfo(chat_user_id).then((data: UserInfo) => {
                    if (data.chat_user_id === state.activeUser) {
                        commit('setChatUserInfo', data);
                        resolve(data);
                    }
                }).catch((err: any) => {
                    console.error(err);
                    reject(err);
                });
            });
        },
    },
};
